/**
 * Reporting a rejected response setting back to the form that sent it.
 *
 * The API answers 400 with a message that names the offending field, and both
 * forms used to swallow it into a bare `window.alert`. `responseSettingError`
 * turns that failure into the two things a form needs: the message to render,
 * and the field it belongs to, read out of the message itself so there is no
 * second list of field names to keep in sync with the backend.
 */

export type ResponseSettingField =
  | 'responseStatusCode'
  | 'responseContentType'
  | 'responseBody'
  | 'responseHeaders';

export interface ResponseSettingError {
  /** The field the API named, or `null` when the message names none of them. */
  field: ResponseSettingField | null;
  /** The message to render: the API's own whenever it sent one. */
  message: string;
}

/**
 * The field names the backend rejects one at a time, longest-scoped first.
 *
 * Order matters twice: the first name a message mentions is the field it belongs
 * to, and a rejection of a header bag quotes the offending header — which could
 * itself contain another field's name — so the bag has to be tested before the
 * shorter names can match inside it.
 */
const FIELDS: ResponseSettingField[] = [
  'responseHeaders',
  'responseStatusCode',
  'responseContentType',
  'responseBody',
];

/**
 * Statuses whose answer carries no body, whatever the user configured. Express
 * drops the body of these answers itself, so the field's contents are never
 * sent — the form has to say so rather than let a saved body silently vanish.
 */
const BODYLESS_STATUS_CODES = new Set([204, 205, 304]);

/**
 * Whether a typed status code is one that answers with no body.
 *
 * The field holds text, so this is driven off the value in it (not a number
 * the form parsed) and adds no request of its own. An empty or non-numeric
 * field is not bodyless.
 */
export function isBodylessStatus(statusCode: string): boolean {
  return BODYLESS_STATUS_CODES.has(Number(statusCode.trim()));
}

/** Reads the message out of a Nest `BadRequestException` body, or anything else. */
export function responseSettingError(error: unknown, fallback: string): ResponseSettingError {
  const data = (error as { response?: { data?: { message?: unknown } } } | undefined)?.response
    ?.data;
  const raw = data?.message;

  // A single message is what the API sends; an array is flattened rather than
  // rendered as `[object Object]` should a list ever appear here.
  const message =
    typeof raw === 'string' && raw.trim() !== ''
      ? raw
      : Array.isArray(raw) && raw.length > 0
        ? raw.join(' ')
        : fallback;

  const field = FIELDS.find((name) => message.includes(name)) ?? null;

  return { field, message };
}

/** One header row as a form holds it: what the user typed, not what is valid. */
export interface ResponseHeaderRow {
  name: string;
  value: string;
}

/**
 * The API's rejection as it belongs to one header row, or `null` when it does
 * not.
 *
 * The backend names the offending header in its message (`… value for
 * \`X-Trace\` must not contain control characters`), so the row carrying that
 * name is the row the message is rendered under. A row whose name is still
 * empty is nobody's row: a bag-level message never names it, and matching every
 * name against `''` would flag the whole list.
 */
export function responseHeaderRowError(
  error: ResponseSettingError | null,
  name: string,
): string | null {
  if (!error || error.field !== 'responseHeaders' || name === '') {
    return null;
  }

  return error.message.includes(name) ? error.message : null;
}

/**
 * The rejection that belongs to no single row — too many headers, or a bag over
 * the size limit — and so is rendered once, above the list.
 */
export function responseHeadersGroupError(
  error: ResponseSettingError | null,
  rows: ReadonlyArray<ResponseHeaderRow>,
): string | null {
  if (!error || error.field !== 'responseHeaders') {
    return null;
  }

  const named = rows.some((row) => responseHeaderRowError(error, row.name) !== null);

  return named ? null : error.message;
}

/**
 * The rows as the API takes them: a bag of names to values.
 *
 * A row the user added and never filled in adds nothing and is dropped;
 * anything typed at all is sent exactly as typed, so the API's own rejection —
 * not a silent drop — is what a blank name or a bad character gets.
 */
export function responseHeaderRowsToBag(
  rows: ReadonlyArray<ResponseHeaderRow>,
): Record<string, string> {
  const bag: Record<string, string> = {};

  for (const row of rows) {
    if (row.name === '' && row.value === '') {
      continue;
    }

    bag[row.name] = row.value;
  }

  return bag;
}

/** The stored bag as form rows, so the form shows what the next capture will send. */
export function responseHeaderRows(stored: unknown): ResponseHeaderRow[] {
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
    return [];
  }

  return Object.entries(stored as Record<string, unknown>)
    .filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    .map(([name, value]) => ({ name, value }));
}
